import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, Loader2, ShieldCheck, Zap } from "lucide-react";
import {
    fetchBillingPackages,
    fetchMyBilling,
    startTrial,
    createCheckout,
    fetchCheckoutStatus,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

const PRO_FEATURES = {
    en: [
        "Unlimited classes",
        "Unlimited AI lesson plans",
        "CSV roster + water log exports",
        "PDF lesson plan exports",
        "Custom school branding",
        "Priority email support",
    ],
    es: [
        "Clases ilimitadas",
        "Planes de lección AI ilimitados",
        "Exportar lista CSV + bitácoras",
        "Exportar planes en PDF",
        "Marca escolar personalizada",
        "Soporte prioritario por email",
    ],
};

const FREE_FEATURES = {
    en: [
        "Full 6-week curriculum",
        "AI chatbot + Water Lab + Plant Picker",
        "1 class, 5 AI lesson plans / month",
        "Live leaderboard + cheers",
    ],
    es: [
        "Currículo completo de 6 semanas",
        "Chatbot AI + Water Lab + Plant Picker",
        "1 clase, 5 planes AI / mes",
        "Tabla en vivo + apoyos",
    ],
};

export default function Pricing() {
    const { lang } = useLang();
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [packages, setPackages] = useState([]);
    const [billing, setBilling] = useState(null);
    const [yearly, setYearly] = useState(true);
    const [busy, setBusy] = useState(null);  // "trial" | "monthly" | "yearly" | "poll"
    const [pollMsg, setPollMsg] = useState(null);
    const [error, setError] = useState(null);
    const proFeat = PRO_FEATURES[lang === "es" ? "es" : "en"];
    const freeFeat = FREE_FEATURES[lang === "es" ? "es" : "en"];

    // Poll Stripe status after redirect.
    const pollStatus = useCallback(
        async (session_id, attempt = 0) => {
            const MAX = 8;
            if (attempt >= MAX) {
                setPollMsg(
                    lang === "es"
                        ? "El pago aún se está procesando. Revisa tu email."
                        : "Payment is still processing. Check your email for the receipt.",
                );
                setBusy(null);
                return;
            }
            try {
                const data = await fetchCheckoutStatus(session_id);
                if (data.payment_status === "paid") {
                    setPollMsg(
                        lang === "es"
                            ? "✅ ¡Pago exitoso! Bienvenido a Teacher Pro."
                            : "✅ Payment successful! Welcome to Teacher Pro.",
                    );
                    setBusy(null);
                    refresh?.();
                    setTimeout(() => nav("/dashboard?upgrade=success"), 1500);
                    return;
                }
                if (data.status === "expired" || data.status === "cancelled") {
                    setPollMsg(
                        lang === "es"
                            ? "❌ La sesión expiró. Intenta de nuevo."
                            : "❌ Session expired. Please try again.",
                    );
                    setBusy(null);
                    return;
                }
                setPollMsg(
                    lang === "es" ? "Procesando pago…" : "Processing payment…",
                );
                setTimeout(() => pollStatus(session_id, attempt + 1), 2000);
            } catch (e) {
                setPollMsg(
                    `${lang === "es" ? "Error" : "Error"}: ${e.response?.data?.detail || e.message}`,
                );
                setBusy(null);
            }
        },
        [lang, nav, refresh],
    );

    useEffect(() => {
        fetchBillingPackages()
            .then((d) => setPackages(d.packages || []))
            .catch(() => setPackages([]));
        if (user) fetchMyBilling().then(setBilling).catch(() => setBilling(null));
    }, [user]);

    useEffect(() => {
        const session_id = searchParams.get("session_id");
        const cancelled = searchParams.get("upgrade") === "cancelled";
        if (session_id) {
            setBusy("poll");
            setPollMsg(lang === "es" ? "Verificando pago…" : "Verifying payment…");
            pollStatus(session_id);
        } else if (cancelled) {
            setError(
                lang === "es"
                    ? "Pago cancelado. Aún puedes empezar la prueba gratis."
                    : "Payment cancelled. You can still start the free trial.",
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStartTrial = async () => {
        if (!user) {
            nav("/signup?next=/pricing");
            return;
        }
        setBusy("trial");
        setError(null);
        try {
            await startTrial();
            await refresh?.();
            nav("/dashboard?trial=started");
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
            setBusy(null);
        }
    };

    const handleCheckout = async (package_id) => {
        if (!user) {
            nav(`/signup?next=/pricing&pkg=${package_id}`);
            return;
        }
        setBusy(package_id);
        setError(null);
        try {
            const { url } = await createCheckout(package_id);
            window.location.href = url;
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
            setBusy(null);
        }
    };

    const monthly = packages.find((p) => p.id === "monthly") || { amount: 9 };
    const yearlyPkg = packages.find((p) => p.id === "yearly") || { amount: 79 };
    const monthlyEquiv = (yearlyPkg.amount / 12).toFixed(2);
    const savings = Math.round(
        (1 - yearlyPkg.amount / 12 / monthly.amount) * 100,
    );
    const isPro = billing?.is_pro;
    const trialUsed = billing?.trial_used;

    return (
        <div className="min-h-screen pt-32 pb-24 px-6" data-testid="pricing-page">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <span className="tag inline-flex items-center gap-1">
                        <Sparkles size={12} className="text-[var(--lime)]" />
                        {lang === "es" ? "// planes" : "// pricing"}
                    </span>
                    <h1
                        className="font-display font-black text-white text-5xl md:text-6xl lg:text-7xl uppercase mt-4 leading-none"
                        data-testid="pricing-title"
                    >
                        {lang === "es" ? "Sube de nivel" : "Power up the block"}
                    </h1>
                    <p className="font-mono text-sm md:text-base text-[var(--muted)] mt-4 max-w-2xl mx-auto">
                        {lang === "es"
                            ? "Todo el currículo es gratis. Para siempre. Sube a Pro cuando necesites más."
                            : "The full curriculum is free. Forever. Upgrade to Pro when you need more firepower."}
                    </p>
                </div>

                {pollMsg && (
                    <div
                        className="card p-4 mb-6 max-w-2xl mx-auto font-mono text-sm text-center"
                        data-testid="pricing-poll-msg"
                    >
                        <span className="inline-flex items-center gap-2 text-white">
                            {busy === "poll" && (
                                <Loader2 size={14} className="animate-spin text-[var(--cyan)]" />
                            )}
                            {pollMsg}
                        </span>
                    </div>
                )}

                {error && (
                    <div
                        className="card p-4 mb-6 max-w-2xl mx-auto font-mono text-sm text-center border-[var(--coral)]"
                        data-testid="pricing-error"
                    >
                        <span className="text-[var(--coral)]">{error}</span>
                    </div>
                )}

                {isPro && (
                    <div
                        className="card p-5 mb-8 max-w-2xl mx-auto text-center border-l-2 border-l-[var(--lime)]"
                        data-testid="pricing-already-pro"
                    >
                        <div className="font-mono text-xs text-[var(--lime)] uppercase tracking-widest">
                            {billing.tier === "trial" ? "Trial active" : "Pro active"}
                        </div>
                        <div className="font-mono text-xs text-[var(--muted)] mt-1">
                            {billing.tier === "trial"
                                ? `${lang === "es" ? "Expira" : "Expires"}: ${billing.trial_ends_at?.slice(0, 10)}`
                                : `${lang === "es" ? "Expira" : "Expires"}: ${billing.subscription_expires_at?.slice(0, 10)}`}
                        </div>
                    </div>
                )}

                {/* Yearly toggle */}
                <div className="flex items-center justify-center gap-3 mb-10">
                    <button
                        type="button"
                        onClick={() => setYearly(false)}
                        className={`font-mono text-xs uppercase tracking-[0.22em] px-4 py-2 border ${
                            !yearly
                                ? "bg-white text-black border-white"
                                : "text-[var(--muted)] border-[var(--border)] hover:text-white"
                        }`}
                        data-testid="toggle-monthly"
                    >
                        {lang === "es" ? "Mensual" : "Monthly"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setYearly(true)}
                        className={`font-mono text-xs uppercase tracking-[0.22em] px-4 py-2 border inline-flex items-center gap-2 ${
                            yearly
                                ? "bg-white text-black border-white"
                                : "text-[var(--muted)] border-[var(--border)] hover:text-white"
                        }`}
                        data-testid="toggle-yearly"
                    >
                        {lang === "es" ? "Anual" : "Yearly"}
                        <span
                            className={`px-1.5 py-0.5 text-[10px] ${yearly ? "bg-[var(--lime)] text-black" : "border border-[var(--lime)] text-[var(--lime)]"}`}
                        >
                            -{savings}%
                        </span>
                    </button>
                </div>

                {/* Plan cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Free */}
                    <div
                        className="card p-7 flex flex-col"
                        data-testid="plan-card-free"
                    >
                        <div className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">
                            {lang === "es" ? "Plan Gratis" : "Free Plan"}
                        </div>
                        <div className="font-display font-black text-white text-5xl mt-3">
                            $0
                        </div>
                        <div className="font-mono text-xs text-[var(--muted)] mt-1">
                            {lang === "es" ? "para siempre" : "forever"}
                        </div>
                        <ul className="font-mono text-sm text-white space-y-2 mt-6 flex-1">
                            {freeFeat.map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <Check
                                        size={16}
                                        className="text-[var(--lime)] mt-0.5 shrink-0"
                                    />
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            disabled
                            className="font-mono text-xs uppercase tracking-[0.22em] border border-[var(--border)] text-[var(--muted)] px-5 py-3 mt-6 cursor-default"
                            data-testid="plan-free-cta"
                        >
                            {lang === "es" ? "Plan actual" : "Current plan"}
                        </button>
                    </div>

                    {/* Pro */}
                    <div
                        className="card p-7 flex flex-col relative overflow-hidden border-l-2 border-l-[var(--lime)]"
                        data-testid="plan-card-pro"
                    >
                        <span
                            className="absolute top-4 right-4 px-2 py-0.5 bg-[var(--lime)] text-black text-[10px] font-mono uppercase tracking-widest font-bold"
                            data-testid="plan-pro-badge"
                        >
                            {lang === "es" ? "Más popular" : "Most popular"}
                        </span>
                        <div className="font-mono text-xs uppercase tracking-widest text-[var(--lime)]">
                            {lang === "es" ? "Teacher Pro" : "Teacher Pro"}
                        </div>
                        <div className="font-display font-black text-white text-5xl mt-3 flex items-baseline gap-2">
                            ${yearly ? monthlyEquiv : monthly.amount}
                            <span className="font-mono text-sm text-[var(--muted)] font-normal">
                                /{lang === "es" ? "mes" : "mo"}
                            </span>
                        </div>
                        <div className="font-mono text-xs text-[var(--muted)] mt-1">
                            {yearly
                                ? `${lang === "es" ? "Facturado anualmente como" : "Billed yearly as"} $${yearlyPkg.amount}`
                                : lang === "es"
                                  ? "Facturado mensualmente"
                                  : "Billed monthly"}
                        </div>
                        <ul className="font-mono text-sm text-white space-y-2 mt-6 flex-1">
                            {proFeat.map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <Check
                                        size={16}
                                        className="text-[var(--lime)] mt-0.5 shrink-0"
                                    />
                                    {f}
                                </li>
                            ))}
                        </ul>

                        {/* CTAs */}
                        <div className="flex flex-col gap-2 mt-6">
                            {!isPro && !trialUsed && (
                                <button
                                    type="button"
                                    onClick={handleStartTrial}
                                    disabled={!!busy}
                                    className="font-mono text-xs uppercase tracking-[0.22em] bg-white text-black px-5 py-3 hover:bg-[var(--lime)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                    data-testid="plan-trial-cta"
                                >
                                    {busy === "trial" && (
                                        <Loader2 size={12} className="animate-spin" />
                                    )}
                                    <ShieldCheck size={14} />
                                    {lang === "es"
                                        ? "Iniciar prueba gratis 14 días"
                                        : "Start 14-day free trial"}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() =>
                                    handleCheckout(yearly ? "yearly" : "monthly")
                                }
                                disabled={!!busy || isPro}
                                className="font-mono text-xs uppercase tracking-[0.22em] border border-[var(--lime)] text-[var(--lime)] px-5 py-3 hover:bg-[var(--lime)] hover:text-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                data-testid={`plan-buy-${yearly ? "yearly" : "monthly"}`}
                            >
                                {(busy === "monthly" || busy === "yearly") && (
                                    <Loader2 size={12} className="animate-spin" />
                                )}
                                <Zap size={14} />
                                {isPro
                                    ? lang === "es"
                                        ? "Ya eres Pro"
                                        : "Already Pro"
                                    : lang === "es"
                                      ? `Comprar ${yearly ? "Anual" : "Mensual"}`
                                      : `Buy ${yearly ? "Yearly" : "Monthly"}`}
                            </button>
                        </div>
                        <p className="font-mono text-[10px] text-[var(--muted)] mt-3 text-center">
                            {lang === "es"
                                ? "Sin tarjeta requerida para la prueba. Cancela cuando quieras."
                                : "No card required for trial. Cancel anytime."}
                        </p>
                    </div>
                </div>

                {/* FAQ */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {[
                        {
                            q: lang === "es" ? "¿Cobran a los estudiantes?" : "Do students pay?",
                            a:
                                lang === "es"
                                    ? "Nunca. Los estudiantes siempre acceden gratis."
                                    : "Never. Students always access free.",
                        },
                        {
                            q: lang === "es" ? "¿Cancelar?" : "How do I cancel?",
                            a:
                                lang === "es"
                                    ? "Tu suscripción no se renueva sola. El acceso dura el periodo que compraste."
                                    : "Your purchase doesn't auto-renew. You keep Pro until the period ends.",
                        },
                        {
                            q: lang === "es" ? "¿Reembolsos?" : "Refunds?",
                            a:
                                lang === "es"
                                    ? "Garantía de 30 días. Escríbenos."
                                    : "30-day refund. Email us.",
                        },
                    ].map((it) => (
                        <div key={it.q} className="font-mono text-sm">
                            <div className="text-white font-bold mb-1">{it.q}</div>
                            <div className="text-[var(--muted)]">{it.a}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
